/**
 * @voltxampere/web — fx overlay renderer (Sprint 29-5)
 *
 * Paints brass-hot meteor particles + wire glow strokes onto a
 * dedicated overlay canvas, sitting on top of the schematic canvas.
 * The schematic renderer (@voltxampere/ui's CanvasRenderer) is
 * unchanged — fx lives in its own canvas so engine zero-diff stays
 * intact and the schematic pipeline keeps its dirty-region
 * optimisation unbothered by per-frame particle redraws.
 *
 * Sprint 29 scope: minimum-viable visual — particle dots + simple
 * glow opacity. Sprint 30 (Voltage Heatmap) will refactor this into a
 * unified overlay layer with proper bezier interpolation, easing
 * fades, and additive blend modes.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Wire } from "@voltxampere/schema";
import {
  reconcileParticles,
  stepMeteorPool,
  particleCountForCurrent,
  type FxFrame,
  type MeteorPool,
} from "@voltxampere/fx";

/** Brass-hot fill from the brand canon. */
const BRASS_HOT = "#E8B35C";

/** Particle radius in CSS pixels at zoom 1. */
const PARTICLE_RADIUS = 2.5;

/**
 * Project a normalized wire-path position [0, 1] into a world-space
 * point along the polyline. Linear segment interpolation; bezier /
 * catenary curves are a Sprint 30+ refinement.
 */
export function pointAlongWire(wire: Wire, t: number): { x: number; y: number } | null {
  if (wire.path.length < 2) return null;
  // Total path length
  const lengths: number[] = [];
  let total = 0;
  for (let i = 1; i < wire.path.length; i++) {
    const a = wire.path[i - 1];
    const b = wire.path[i];
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    lengths.push(len);
    total += len;
  }
  if (total === 0) return null;
  // Walk segments until we cover t × total
  const target = Math.max(0, Math.min(1, t)) * total;
  let acc = 0;
  for (let i = 0; i < lengths.length; i++) {
    const segLen = lengths[i] ?? 0;
    if (acc + segLen >= target) {
      const a = wire.path[i];
      const b = wire.path[i + 1];
      if (!a || !b) return null;
      const localT = segLen > 0 ? (target - acc) / segLen : 0;
      return {
        x: a.x + (b.x - a.x) * localT,
        y: a.y + (b.y - a.y) * localT,
      };
    }
    acc += segLen;
  }
  return wire.path[wire.path.length - 1] ?? null;
}

export interface FxOverlayDrawOpts {
  readonly viewport: {
    readonly center: { x: number; y: number };
    readonly zoom: number;
  };
  readonly cssWidth: number;
  readonly cssHeight: number;
  readonly dpr: number;
  /** Wires keyed by id from the active circuit. */
  readonly wires: ReadonlyMap<string, Wire>;
}

/**
 * Reconcile per-wire particle counts to match the current frame's
 * fx state. Wires with current below the noise floor get 0 particles
 * (and therefore disappear from the overlay).
 */
export function reconcileFromFrame(pool: MeteorPool, frame: FxFrame): void {
  // Add / shrink lists for wires present in the frame
  const seen = new Set<string>();
  for (const [wireId, wireFx] of frame.wires) {
    seen.add(wireId);
    const target = particleCountForCurrent(wireFx.current);
    reconcileParticles(pool, wireId, target);
  }
  // Drop pools for wires no longer in the frame (preset switch, undo, …)
  for (const wireId of pool.perWire.keys()) {
    if (!seen.has(wireId)) reconcileParticles(pool, wireId, 0);
  }
}

/**
 * Step the pool forward by `dt` seconds and paint the resulting state
 * onto the fx canvas context. `reducedMotion` short-circuits the step
 * (particles freeze) but still draws the static frame so users with
 * accessibility preferences set still see WHERE the current is, just
 * without the motion.
 */
export function drawFxOverlay(
  ctx: CanvasRenderingContext2D,
  pool: MeteorPool,
  frame: FxFrame,
  dt: number,
  reducedMotion: boolean,
  opts: FxOverlayDrawOpts,
): void {
  // Build the fx hint map for the meteor stepper
  const hintMap = new Map<string, { current: number; direction: 1 | -1 | 0 }>();
  for (const [wireId, wireFx] of frame.wires) {
    hintMap.set(wireId, { current: wireFx.current, direction: wireFx.direction });
  }
  stepMeteorPool(pool, hintMap, dt, reducedMotion);

  ctx.save();
  // Sprint 30 — clear pass moved to the caller (vxa-canvas tick) so the
  // voltage heatmap (Sprint 30-3) can paint BEFORE the particles in the
  // same frame. Caller responsibility: clearRect + setTransform before
  // calling drawFxOverlay.
  ctx.setTransform(opts.dpr, 0, 0, opts.dpr, 0, 0);
  // World transform — mirrors the schematic renderer
  const z = opts.viewport.zoom;
  ctx.translate(opts.cssWidth / 2, opts.cssHeight / 2);
  ctx.scale(z, z);
  ctx.translate(-opts.viewport.center.x, -opts.viewport.center.y);

  ctx.fillStyle = BRASS_HOT;

  for (const [wireId, particles] of pool.perWire) {
    const wire = opts.wires.get(wireId);
    if (!wire) continue;
    const wireFx = frame.wires.get(wireId);
    if (!wireFx || wireFx.current === 0) continue;
    // Glow opacity scales the particle alpha (Sprint 29-4 frozen mapping)
    const baseAlpha = 0.5 + 0.5 * wireFx.glow;
    for (const p of particles) {
      const point = pointAlongWire(wire, p.position);
      if (!point) continue;
      ctx.globalAlpha = baseAlpha;
      ctx.beginPath();
      ctx.arc(point.x, point.y, PARTICLE_RADIUS / z, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}
